import axiosBase from "axios";
import Cookies from "js-cookie";
import { getRuntimeEnv } from "vite-runtime-env-script-plugin/getRuntimeEnv";

import { apisUrlsData } from "@/data";

import { logoutHandler } from "./utils";

declare module "axios" { export interface AxiosRequestConfig { isAdmin?: boolean } }

const BACKEND_URL = getRuntimeEnv("BACKEND_URL");

const ADMIN_BACKEND_URL = getRuntimeEnv("ADMIN_BACKEND_URL");

// A flag to avoid multiple simultaneous refresh token requests.
let isRefreshing = false;

// A list of subscribers (callbacks) to be called when the token is refreshed.
let refreshSubscribers: ((token: string) => void)[] = []; // eslint-disable-line

// A function to add a new subscriber to the list.
const subscribeRefreshTokenHandler = (cb: (token: string) => void): void => { refreshSubscribers.push(cb); } // eslint-disable-line

// Call all subscribers with the new token.
const onRefreshedHandler = (token: string): void => {
    refreshSubscribers.forEach((cb) => cb(token));

    refreshSubscribers = [];
};

export const axios = axiosBase.create({
    baseURL: BACKEND_URL,
    timeout: 10000,
});

axios.interceptors.request.use(
    (config) => {
        if (config.isAdmin) config.baseURL = ADMIN_BACKEND_URL; // eslint-disable-line
        else config.baseURL = BACKEND_URL; // eslint-disable-line

        const token = Cookies.get("accessToken");

        const refreshToken = Cookies.get("refreshToken");

        if (config.url && config.url.includes(apisUrlsData.auth.logout)) {
            if (refreshToken) config.headers.Authorization = `Bearer ${refreshToken}`; // eslint-disable-line
        } else
            if (token) config.headers.Authorization = `Bearer ${token}`; // eslint-disable-line
        if (config.data instanceof FormData) config.headers["Content-Type"] = "multipart/form-data"; // eslint-disable-line
        else config.headers["Content-Type"] = "application/json"; // eslint-disable-line

        return config;
    },
    (error) => Promise.reject(error),
);

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If the error is a 401 and the request has not been retried yet, try to refresh the token.
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Check if there is a refresh token request already in progress in the background from another interceptor.
            if (isRefreshing) {
                return new Promise((resolve) => {
                    subscribeRefreshTokenHandler((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(axios(originalRequest));
                    });
                });
            }

            // If there is no refresh token request in progress, set the _retry flag to true and start a new request. This prevents the same request from being retried multiple times.
            originalRequest._retry = true;

            // Set the isRefreshing flag to true to indicate that a refresh token request is in progress. This prevents multiple simultaneous refresh token requests from being sent.
            isRefreshing = true;

            try {
                const refreshToken = Cookies.get("refreshToken");

                if (!refreshToken) {
                    await logoutHandler(
                        undefined,
                        undefined,
                        originalRequest.isAdmin,
                    );

                    throw new Error("Refresh Token is Missing");
                }

                const response = await axiosBase.post(
                    `${BACKEND_URL + apisUrlsData.auth.refreshToken}`,
                    {},
                    { headers: { Authorization: `Bearer ${refreshToken}` } },
                )["catch"](async () => {
                    await logoutHandler(
                        undefined,
                        undefined,
                        originalRequest.isAdmin,
                    );

                    throw new Error("Refresh Token is Invalid");
                });

                const newAccessToken = response.data.accessToken;

                const newRefreshToken = response.data.refreshToken;

                Cookies.set(
                    "accessToken",
                    newAccessToken,
                    { secure: true },
                );

                Cookies.set(
                    "refreshToken",
                    newRefreshToken,
                    { secure: true },
                );

                axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

                onRefreshedHandler(newAccessToken);

                return axios(originalRequest);
            } catch (refreshError) {
                if (axiosBase.isAxiosError(refreshError) && (refreshError.response?.status === 401 || refreshError.response?.status === 403)) {
                    await logoutHandler(
                        undefined,
                        undefined,
                        originalRequest.isAdmin,
                    );

                    return Promise.reject(new Error("Session Expired. Please log in again."));
                }
            } finally { isRefreshing = false; }
        }

        return Promise.reject(error);
    },
);
